import { gql } from 'apollo-angular';

/**
 * Company schema
 */
export class CompanySchema {
	/**
	 * Official properties
	 */
	static readonly officialProperties = `
		uid
		name
		eMailAddress
		website
		phoneNumber
		faxNumber
		billingAddress {
			address1
			address2
			city
			zipCode
			country {
				code
				name
			}
			region {
				name
				regionCode
				countryCode
			}
			latitude
			longitude
		}
		materialClasses {
			uid
			name
			displayName
		}
		restorations {
			uid
			name
			displayName
		}
		shades {
			uid
			displayGuide
			guide
		}
		offerings {
			uid
			displayName
			name
		}
		services {
			uid
			displayName
			name
		},
		additionalOrderInformationTemplates {
			uid
			id
			type
			name
			isActivated
			isMandatory
			position
			possibleValues
			defaultValue
		}
		availableInCountries {
			code
			name
		}
	`;

	/**
	 * All properties
	 *
	 * To get all properties of a company the API must return a FullCompany or the data must be requested via User
	 */
	static readonly fullLevelProperties = `
		uid
		name
		eMailAddress
		website
		phoneNumber
		faxNumber
		listrange
		timezone
		languageCode
		billingAddress {
			address1
			address2
			city
			zipCode
			country {
				code
				name
			}
			region {
				name
				regionCode
				countryCode
			}
			latitude
			longitude
		}
		shippingAddress {
			address1
			address2
			city
			zipCode
			country {
				code
				name
			}
			region {
				name
				regionCode
				countryCode
			}
			latitude
			longitude
		}
		materialClasses {
			uid
			name
			displayName
		}
		restorations {
			uid
			name
			displayName
		}
		shades {
			uid
			displayGuide
			guide
		}
		offerings {
			uid
			displayName
			name
		}
		services {
			uid
			displayName
			name
		}
		additionalOrderInformationTemplates {
			uid
			id
			type
			name
			isActivated
			isMandatory
			position
			possibleValues
			defaultValue
		}
		availableInCountries {
			code
			name
		}
	`;

	static readonly editLabProfileProperties = `
		uid
		materialClasses {
			uid
			name
			displayName
		}
		restorations {
			uid
			name
			displayName
		}
		shades {
			uid
			displayGuide
			guide
		}
		offerings {
			uid
			displayName
			name
		}
		services {
			uid
			displayName
			name
		}
		availableInCountries {
			code
			name
		}
	`;

	static readonly favoriteContractorProperties = `
		uid
		name
		eMailAddress
		website
		phoneNumber
		faxNumber
		listrange
		timezone
		favoriteContractors {
			uid
			name
			eMailAddress
			offerings {
				uid
				displayName
			}
			services {
				uid
				displayName
			}
			billingAddress {
				address1
				address2
				city
				zipCode
				country {
					code
					name
				}
				region {
					name
					regionCode
					countryCode
				}
				latitude
				longitude
			}
		}
	`;

	static readonly labLockProperties = `
		uid
		labLockEntries {
			activationDate
			dentist {
				uid
				name
				eMailAddress
				website
				phoneNumber
				faxNumber
				billingAddress {
					address1
					address2
					city
					zipCode
				}
			}
			inventoryNumber
			labLockState
			uid
			workstationId
		}
	`;

	/**
	 * Properties for company edit view
	 */
	static readonly companyForWelcome = `
		uid
		materialClasses {
			uid
			name
			displayName
		}
		restorations {
			uid
			name
			displayName
		}
		shades {
			uid
			displayGuide
			guide
		}
		offerings {
			uid
			displayName
			name
		}
		services {
			uid
			displayName
			name
		}
		additionalOrderInformationTemplates {
			uid
			id
			type
			name
			isActivated
			isMandatory
			position
			possibleValues
			defaultValue
		}
	`;

	/**
	 * Properties for company edit view
	 */
	static readonly companyForEditProperties = `
		uid
		name
		phoneNumber
		faxNumber
		eMailAddress
		website
		languageCode
		timezone
		listrange
		anonymizePatientByDefault
	`;

	/**
	 * Properties for company edit billing address and shipping address in view
	 */
	static readonly companyForEditAddressProperties = `
		uid
		billingAddress {
			address1
			address2
			city
			zipCode
			country {
				code
				name
			}
			region {
				name
				regionCode
				countryCode
			}
			latitude
			longitude
		}
		shippingAddress {
			address1
			address2
			city
			zipCode
			country {
				code
				name
			}
			region {
				name
				regionCode
				countryCode
			}
			latitude
			longitude
		}
	`;

	/**
	 * Properties for company edit shipping address in view
	 */
	static readonly companyForEditBillingAddressProperties = `
		uid
		billingAddress {
			address1
			address2
			city
			zipCode
			country {
				code
				name
			}
			region {
				name
				regionCode
				countryCode
			}
			latitude
			longitude
		}
	`;

	/**
	 * Properties for company edit shipping address in view
	 */
	static readonly companyForAdditionalOrderInformation = `
		uid
		additionalOrderInformationTemplates {
			uid
			id
			type
			name
			isActivated
			isMandatory
			position
			possibleValues
			defaultValue
		}
	`;

	/**
	 * ListCount of company elements
	 */
	static readonly listCount = `
		list {
			displayName
			name
			uid
		}
		totalCount
	`;

	static readonly companyList = gql`
		query Companylist($filters: [WhereFilter], $pageIndex: Int, $resultsPerPage: Int) {
			companylist(filters: $filters, pageIndex: $pageIndex, resultsPerPage: $resultsPerPage) {
				list {
						${CompanySchema.officialProperties}
				}
				totalCount
			}
		}
	`;

	/**
	 * Mutation: updateCompany
	 */
	static readonly updateCompany = gql`
		mutation UpdateCompany($company: UpdateCompanyInput!) {
			updateCompany(company: $company) {
				${CompanySchema.fullLevelProperties}
			}
		}
	`;

	/**
	 * Mutation: updateCompany for company edit view
	 */
	static readonly updateCompanyForEdit = gql`
		mutation UpdateCompany($company: UpdateCompanyInput!) {
			updateCompany(company: $company) {
				${CompanySchema.companyForEditProperties}
			}
		}
	`;

	/**
	 * Mutation: updateCompany for company edit address view
	 */
	static readonly updateCompanyForEditAddress = gql`
		mutation UpdateCompany($company: UpdateCompanyInput!) {
			updateCompany(company: $company) {
				${CompanySchema.companyForEditAddressProperties}
			}
		}
	`;

	/**
	 * Mutation: updateCompany for company edit address view
	 */
	static readonly updateCompanyForEditBillingAddress = gql`
		mutation UpdateCompany($company: UpdateCompanyInput!) {
			updateCompany(company: $company) {
				${CompanySchema.companyForEditBillingAddressProperties}
			}
		}
	`;

	/**
	 * Mutation: updateCompany for company AdditionalOrderInformation
	 */
	static readonly updateCompanyForAdditionalOrderInformation = gql`
		mutation UpdateCompany($company: UpdateCompanyInput!) {
			updateCompany(company: $company) {
				${CompanySchema.companyForAdditionalOrderInformation}
			}
		}
	`;

	/**
	 * Query: company
	 */
	static readonly getByUid = gql`
		query GetCompany($uid: String!){
			company(uid: $uid){
				${CompanySchema.officialProperties}
			}
		}
	`;

	/**
	 * Mutation: companySetState
	 */
	static readonly companySetState = gql`
		mutation CompanySetState($uid: ID!, $state: CompanyState!) {
			companySetState(uid: $uid, state: $state) {
				state
			}
		}
	`;

	/**
	 * Query: getFavorizingPurchasers with Pagination
	 */
	static readonly getFavorizingPurchasers = gql`
		query GetFavorizingPurchasers(
			$includeSleepingLabs: Boolean!
			$whereFilter: [WhereFilter]
			$pageIndex: Int
			$resultsPerPage: Int
			$onlyTotalCount: Boolean
			$orderBy: String
			$sortDirection: sortDirection
		) {
			getFavorizingPurchasers(
				includeSleepingLabs: $includeSleepingLabs
				whereFilter: $whereFilter
				pageIndex: $pageIndex
				resultsPerPage: $resultsPerPage
				onlyTotalCount: $onlyTotalCount
				orderBy: $orderBy
				sortDirection: $sortDirection
			) {
				list {
					uid
					name
					eMailAddress
					phoneNumber
					billingAddress {
						address1
						address2
						city
						zipCode
						country {
							code
							name
						}
						region {
							name
							regionCode
							countryCode
						}
						latitude
						longitude
					}
				}
				totalCount
			}
		}
	`;

	/**
	 * Mutation: addReceiversToFavorites
	 */
	static readonly addFavoriteContractor = gql`
		mutation AddFavoriteContractor ($favoriteContractorUid: ID!) {
			addFavoriteContractor(favoriteContractorUid: $favoriteContractorUid) {
				${CompanySchema.favoriteContractorProperties}
			}
		}
	`;

	/**
	 * Mutation: removeReceiversFromFavorites
	 */
	static readonly removeFavoriteContractor = gql`
		mutation RemoveFavoriteContractor ($favoriteContractorUid: ID!) {
			removeFavoriteContractor(favoriteContractorUid: $favoriteContractorUid) {
				${CompanySchema.favoriteContractorProperties}
			}
		}
	`;

	/**
	 * Mutation: removeReceiversFromFavorites
	 */
	static readonly removeFavorizingPurchaser = gql`
		mutation RemoveFavorizingPurchaser ($favorizingPurchaserUid: ID!) {
			removeFavorizingPurchaser(favorizingPurchaserUid: $favorizingPurchaserUid) {
				${CompanySchema.officialProperties}
			}
		}
	`;

	/**
	 * Query: searchContractors
	 */
	static readonly searchContractors = gql`
		query SearchContractors(
			$radialSearch: radialSearch
			$serviceFilter: [ID]
			$offeringFilter: [ID]
			$whereFilter: [WhereFilter]
			$pageIndex: Int
			$resultsPerPage: Int
			$onlyTotalCount: Boolean
			$orderBy: String
			$sortDirection: sortDirection
		) {
			searchContractors(
				radialSearch: $radialSearch
				serviceFilter: $serviceFilter
				offeringFilter: $offeringFilter
				whereFilter: $whereFilter
				pageIndex: $pageIndex
				resultsPerPage: $resultsPerPage
				onlyTotalCount: $onlyTotalCount
				orderBy: $orderBy
				sortDirection: $sortDirection
			) {
				list {
					uid
					name
					eMailAddress
					externalPartner {
						internalPartnerName
						name
					}
					website
					phoneNumber
					faxNumber
					billingAddress {
						address1
						address2
						city
						zipCode
						country {
							code
							name
						}
						region {
							name
							regionCode
							countryCode
						}
						latitude
						longitude
					}
				}
				totalCount
			}
		}
	`;
}
